import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { anchorFrom, useAnchoredPanel, type AnchorRect } from '../lib/anchored-panel';
import { Input } from './Input';
import popoverStyles from './Popover.module.css';
import selectStyles from './Select.module.css';
import styles from './SuggestInput.module.css';

// A text field that offers names it has seen before (F19.14: box 1's "Tools or systems"). Type freely — a new name simply
// stays as typed — or pick a match from the list anchored under the field (CLAUDE.md §5) with the mouse or the arrow keys
// and Enter. Ours, never a bare input with a browser datalist, which ignores the palette and cannot be styled.

/** Past this the list stops: typing a letter or two narrows it faster than scrolling. */
const SHOWN_MAX = 8;

interface SuggestInputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly suggestions: readonly string[];
  readonly 'aria-label': string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly isInvalid?: boolean;
  readonly maxLength?: number;
  readonly id?: string;
}

/** Matches ignoring case and spacing: names that start with what is typed first, then names that contain it. */
export function suggestionsFor(typed: string, suggestions: readonly string[]): readonly string[] {
  const query = typed.replace(/\s+/g, ' ').trim().toLowerCase();
  if (query === '') return suggestions.slice(0, SHOWN_MAX);
  const starts: string[] = [];
  const contains: string[] = [];
  for (const name of suggestions) {
    const key = name.replace(/\s+/g, ' ').trim().toLowerCase();
    if (key === query) continue;
    if (key.startsWith(query)) starts.push(name);
    else if (key.includes(query)) contains.push(name);
  }
  return [...starts, ...contains].slice(0, SHOWN_MAX);
}

export function SuggestInput({ value, onChange, suggestions, 'aria-label': ariaLabel, placeholder, disabled = false, isInvalid = false, maxLength, id }: SuggestInputProps): ReactNode {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [active, setActive] = useState(-1);
  const matches = anchor === null ? [] : suggestionsFor(value, suggestions);
  const isOpen = matches.length > 0;
  const style = useAnchoredPanel(isOpen ? anchor : null, panelRef, 'left', width);

  function open(): void {
    const wrap = wrapRef.current;
    if (!wrap || disabled) return;
    setAnchor(anchorFrom(wrap));
    setWidth(wrap.getBoundingClientRect().width);
  }

  function close(): void {
    setAnchor(null);
    setActive(-1);
  }

  function pick(name: string): void {
    onChange(name);
    close();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (anchor === null) {
        open();
        return;
      }
      if (matches.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + matches.length) % matches.length);
      return;
    }
    if (event.key === 'Enter' && isOpen && active >= 0) {
      const name = matches[active];
      if (name === undefined) return;
      event.preventDefault();
      pick(name);
      return;
    }
    if (event.key === 'Escape' && isOpen) {
      // Only the list closes; a drawer or dialog around the field stays open.
      event.stopPropagation();
      close();
      return;
    }
    if (event.key === 'Tab') close();
  }

  const activeId = isOpen && active >= 0 ? `${listId}-${active}` : undefined;
  return (
    <div ref={wrapRef} className={styles.wrap}>
      <Input
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        aria-activedescendant={activeId}
        autoComplete="off"
        isInvalid={isInvalid}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onFocus={open}
        onBlur={close}
        onKeyDown={onKeyDown}
        onChange={(event) => {
          onChange(event.target.value);
          setActive(-1);
          if (anchor === null) open();
        }}
      />
      {isOpen &&
        createPortal(
          <div ref={panelRef} className={popoverStyles.panel} style={style} data-popover>
            <div id={listId} role="listbox" aria-label={`${ariaLabel}: names used before`}>
              {matches.map((name, at) => (
                <div
                  key={name}
                  id={`${listId}-${at}`}
                  role="option"
                  aria-selected={at === active}
                  className={at === active ? `${selectStyles.option} ${selectStyles.optionActive}` : selectStyles.option}
                  // Keeps the focus in the field, so the pick lands before the blur closes the list.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(at)}
                  onClick={() => pick(name)}
                >
                  <span className={selectStyles.optionText}>{name}</span>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
